import React, { useContext, useEffect, useState } from 'react'
import {
    Table,
    Button,
    Dropdown,
    Typography,
    Affix,
    Form,
    Modal,
    App,
    Space,
    Input
} from 'antd'
import { isMobileOnly } from "react-device-detect"
import _ from "lodash";
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaEllipsisH, FaSearch, FaTimesCircle } from "react-icons/fa"
import { API, Enum, AntDesign } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

// Components
import CURecord from './cu-record'
import AddPermission from './add-permission'

const { Pages: { UserManagement: { Role: RoleLanguage, Permission: PermissionLanguage } }, Share, Message: MessageLanguage } = language
const {  USER_MANAGEMENT_ROLE_PAGINATE, USER_MANAGEMENT_ROLE_REMOVE, USER_MANAGEMENT_ROLE_PERMISSION_PAGINATE, USER_MANAGEMENT_ROLE_PERMISSION_REMOVE } = API
const { Message, RoleComponent } = Enum
const { theme: { token: { colorPrimary } } } = AntDesign

interface PermissionType {
    id: string,
    name: string,
    caption: string,
}
interface RoleType {
    id: string,
    name: string,
    caption: string,
    permissions: PermissionType[],
}

export default function Index() {

    const [, forceUpdate] = useState({})
    const [form] = Form.useForm()
    const [roles, setRoles] = useState<RoleType[]>([])
    const [permissions, setPermissions] = useState<PermissionType[]>([])
    const [record, setRecord] = useState<RoleType|null>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [sort, setSort] = useState<string|false|null|undefined>('createdAt,desc')
    const [name, setName] = useState<string>()
    const [caption, setCaption] = useState<string>()
    const [component, setComponent] = useState<string|null>(null)
    const [paginationRole, setPaginationRole] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const [paginationPermission, setPaginationPermission] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp();

    const roleColumns: ColumnsType<RoleType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: RoleLanguage.name,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={RoleLanguage.name}
                        onChange={({ target: { value: name } }) => !name && setName(name)}
                        onBlur={({ target: { value: name } }) => setName(name)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={name && colorPrimary} />
        },
        {
            title: RoleLanguage.caption,
            dataIndex: 'caption',
            key: 'caption',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={RoleLanguage.caption}
                        onChange={({ target: { value: caption } }) => !caption && setCaption(caption)}
                        onBlur={({ target: { value: caption } }) => setCaption(caption)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={caption && colorPrimary} />
        },

        {
            title: RoleLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD')
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'edit',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setComponent(RoleComponent.CURecord)
                            setIsModalOpen(true)
                        }}>{ Share.edit }</Typography.Link>,
                    },
                    {
                        key: 'add-permission',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setComponent(RoleComponent.AddPermission)
                            setIsModalOpen(true)
                        }}>{ RoleLanguage.addPermissions }</Typography.Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(USER_MANAGEMENT_ROLE_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = paginationRole
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                        sort,
                                    };
                                    getRoles(params)
                                    message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error;
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const permissionColumns: ColumnsType<PermissionType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: PermissionLanguage.name,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: PermissionLanguage.caption,
            dataIndex: 'caption',
            key: 'caption',
        },
        {
            title: Share.delete,
            dataIndex: 'delete',
            key: 'delete',
            render: (text, permission) => <Typography.Link onClick={() => modal.confirm({
                title: Share.deleteConfirmTitle,
                okText: Share.deleteConfirmOk,
                okType: 'danger',
                cancelText: Share.deleteConfirmCancel,
                onOk() {
                    setLoadingRecords(true)
                    axios.delete(USER_MANAGEMENT_ROLE_PERMISSION_REMOVE + record?.id + '/' + permission?.id).then((response: {}) => {
                        const { total = 1, pageSize = 1, current = 1 } = paginationPermission
                        const params = {
                            page: total % pageSize !== 1 ? current : current - 1,
                            limit: pageSize,
                        };
                        getPermissions(params)
                        message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                    }).catch((error: AxiosError) => {
                        const { response: { data } } = error;
                        // @ts-ignore
                        message.error(MessageLanguage[data.message]).then(response => response )
                        setLoadingRecords(false)
                    })
                }
            })}>{<FaTimesCircle size={18} color={'red'} />}</Typography.Link>
        },
    ]

    const getPermissions = (params: AntTableParams) => {
        if (record) {
            setLoadingRecords(true)
            axios.post(USER_MANAGEMENT_ROLE_PERMISSION_PAGINATE + record?.id, {}, { params }).then((response: AxiosResponsePaginate) => {
                const { data: { docs: permissions, page: current, limit: pageSize, totalDocs: total } } = response
                let row = (current - 1) * pageSize;
                setLoadingRecords(false)
                setPaginationPermission({
                    ...paginationPermission,
                    current,
                    pageSize,
                    total,
                })
                setPermissions(permissions.map<PermissionType>((permission: PermissionType) => ({ ...permission, row: ++row, key: permission.id })))
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response )
                setLoadingRecords(false)
            })
        } else setPermissions([]);
    }

    const getRoles = (params: AntTableParams) => {
        setLoadingRecords(true)
        const filters: {
            name?: string,
            caption?: string,
        } = {};
        if (name && !_.isEmpty(name)) filters.name = name
        if (caption && !_.isEmpty(caption)) filters.caption = caption
        axios.post(USER_MANAGEMENT_ROLE_PAGINATE, filters, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: roles, page: current, limit: pageSize, totalDocs: total } } = response
            const { sort } = params
            let row = (current - 1) * pageSize;
            setRoles(roles.map<RoleType>((role: RoleType) => ({ ...role, row: ++row, key: role.id })))
            setLoadingRecords(false)
            setSort(sort)
            setPaginationRole({
                ...paginationRole,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
    }, []);

    useEffect(() => {
        const { current, pageSize } = paginationRole
        getRoles({ page: current, limit: pageSize, sort })
    }, [name, caption]);

    useEffect(() => {
        getPermissions({ page: 1, limit: 10 })
    }, [record]);

    const handleTableRoleChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getRoles({ page: current, limit: pageSize, sort });
    };

    const handleTablePermissionChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getPermissions({ page: current, limit: pageSize, sort });
    };

    return (
        <>
            <Modal destroyOnClose={true} title={
                (component === RoleComponent.AddPermission) ? `${RoleLanguage.addPermissions} (${record?.caption})` :
                Share.addOrEditRecord
            } width={
                (component === RoleComponent.AddPermission) ? 700 :
                450
            } cancelText={Share.cancel} okText={Share.save} open={isModalOpen} onOk={() => form.submit()} onCancel={() => {
                setComponent(null)
                setIsModalOpen(false)
            }}>
                { (component === RoleComponent.CURecord) && <CURecord Form={Form} form={form} record={record} getRoles={getRoles} paginationRole={paginationRole} getPermissions={getPermissions} sort={sort} /> }
                { (component === RoleComponent.AddPermission) && <AddPermission Form={Form} form={form} record={record} getRoles={getRoles} paginationRole={paginationRole} getPermissions={getPermissions} sort={sort} /> }
            </Modal>
            <Table
                columns={roleColumns}
                dataSource={roles}
                pagination={paginationRole}
                loading={loadingRecords}
                onChange={handleTableRoleChange}
                scroll={{ x: true }}
                expandable={{
                    expandedRowKeys: record ? [record?.id] : [],
                    onExpand: (expanded, record) => expanded ? setRecord(record) : setRecord(null),
                    expandedRowRender: (record) => (
                        <Table
                            columns={permissionColumns}
                            dataSource={permissions}
                            pagination={paginationPermission}
                            loading={loadingRecords}
                            onChange={handleTablePermissionChange}
                            scroll={{ x: true }}
                            className={'user-management-second-table'}
                        />
                    ),
                }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                <Button key='add' type="primary" style={{height:'40px'}} icon={<FaPlus />} onClick={() => {
                    setRecord(null)
                    setComponent(RoleComponent.CURecord)
                    setIsModalOpen(true)
                }}>{ Share.add }</Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
